import { getApiBaseUrl } from "./api";

export type SessionUser = {
  id: string;
  email: string;
  displayName: string | null;
  status: string;
  homeTenantId: string;
};

export type SessionInfo = {
  id: string;
  homeTenantId: string;
  absoluteExpiresAt: string;
  idleExpiresAt: string;
  assuranceLevel?: string | null;
  authMethod?: string | null;
  mfaVerifiedAt?: string | null;
};

export type AuthSessionResult = {
  user: SessionUser;
  session: SessionInfo;
  csrfToken: string;
};

export type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | {
      status: "authenticated";
      user: SessionUser;
      session: SessionInfo;
      csrfToken: string;
    }
  | { status: "error"; message: string };

type ApiErrorBody = {
  error?: { code?: string; message?: string };
};

/** In-memory CSRF only — never persist auth secrets in browser web storage. */
let memoryCsrfToken: string | null = null;

export function getMemoryCsrfToken(): string | null {
  return memoryCsrfToken;
}

export function setMemoryCsrfToken(token: string | null): void {
  memoryCsrfToken = token;
}

/**
 * Prefer same-origin (Vite proxy / production BFF) so host-only cookies work cleanly.
 * Empty VITE_API_BASE_URL or production default → same-origin.
 */
function apiBase(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv !== undefined && fromEnv.trim() === "") return "";
  if (import.meta.env.PROD) return getApiBaseUrl();
  if (fromEnv?.trim()) return fromEnv.trim().replace(/\/$/, "");
  if (import.meta.env.DEV) return "";
  return getApiBaseUrl();
}

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const csrf = memoryCsrfToken;
  if (csrf && init.method && init.method.toUpperCase() !== "GET") {
    headers.set("X-CSRF-Token", csrf);
  }
  return fetch(`${apiBase()}${path}`, {
    ...init,
    headers,
    credentials: "include",
    cache: "no-store",
  });
}

function readErrorMessage(body: ApiErrorBody, fallback: string): string {
  return body.error?.message ?? fallback;
}

function parseSessionEnvelope(
  body: Record<string, unknown>
): AuthSessionResult | null {
  const user = body.user as SessionUser | undefined;
  const session = body.session as SessionInfo | undefined;
  const csrfToken = typeof body.csrfToken === "string" ? body.csrfToken : undefined;
  if (!user || !session || !csrfToken) return null;
  return { user, session, csrfToken };
}

function applySession(result: AuthSessionResult): AuthSessionResult {
  setMemoryCsrfToken(result.csrfToken);
  return result;
}

export async function fetchCurrentSession(): Promise<
  | { ok: true; user: SessionUser; session: SessionInfo }
  | { ok: false; status: number }
> {
  const res = await authFetch("/v1/auth/me");
  if (!res.ok) return { ok: false, status: res.status };
  const body = (await res.json()) as { user: SessionUser; session: SessionInfo };
  return { ok: true, user: body.user, session: body.session };
}

/**
 * Reload-safe CSRF recovery. Requires an existing MFA-assured session cookie.
 * Stores the raw token in module memory only.
 */
export async function bootstrapCsrfToken(): Promise<
  | { ok: true; csrfToken: string }
  | { ok: false; status: number }
> {
  const res = await authFetch("/v1/auth/csrf", {
    method: "POST",
    body: "{}",
  });
  if (!res.ok) return { ok: false, status: res.status };
  const body = (await res.json().catch(() => ({}))) as { csrfToken?: string };
  if (!body.csrfToken) return { ok: false, status: res.status };
  setMemoryCsrfToken(body.csrfToken);
  return { ok: true, csrfToken: body.csrfToken };
}

/**
 * Dashboard is ready only after /me and CSRF bootstrap both succeed.
 * /me requires MFA assurance — password-only / preauth cookies do not count.
 */
export async function restoreAuthenticatedSession(): Promise<
  | { ok: true; user: SessionUser; session: SessionInfo; csrfToken: string }
  | { ok: false; status: number }
> {
  const me = await fetchCurrentSession();
  if (!me.ok) {
    setMemoryCsrfToken(null);
    return { ok: false, status: me.status };
  }
  const csrf = await bootstrapCsrfToken();
  if (!csrf.ok) {
    setMemoryCsrfToken(null);
    return { ok: false, status: csrf.status };
  }
  return {
    ok: true,
    user: me.user,
    session: me.session,
    csrfToken: csrf.csrfToken,
  };
}

/**
 * Password step. Server sets HttpOnly preauth cookie when MFA is required.
 * May return a full MFA session only if the API ever issues one at login.
 */
export async function loginWithPassword(
  email: string,
  password: string
): Promise<
  | { ok: true; mfaRequired: true }
  | ({ ok: true; mfaRequired?: false } & AuthSessionResult)
  | { ok: false; message: string; status: number }
> {
  const res = await authFetch("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody & {
    mfaRequired?: boolean;
    user?: SessionUser;
    session?: SessionInfo;
    csrfToken?: string;
  };

  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      message: readErrorMessage(body, "Sign-in failed"),
    };
  }

  if (body.mfaRequired === true) {
    return { ok: true, mfaRequired: true };
  }

  const session = parseSessionEnvelope(body as Record<string, unknown>);
  if (!session) {
    return {
      ok: false,
      status: res.status,
      message: readErrorMessage(body, "Sign-in failed"),
    };
  }
  return { ok: true, ...applySession(session) };
}

export async function verifyTotpMfa(
  code: string
): Promise<
  | ({ ok: true } & AuthSessionResult)
  | { ok: false; message: string; status: number; code?: string }
> {
  const res = await authFetch("/v1/auth/mfa/totp/verify", {
    method: "POST",
    body: JSON.stringify({ totpCode: code }),
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody & Record<string, unknown>;
  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      code: body.error?.code,
      message: readErrorMessage(body, "Verification failed"),
    };
  }
  const session = parseSessionEnvelope(body);
  if (!session) {
    return { ok: false, status: res.status, message: "Verification failed" };
  }
  return { ok: true, ...applySession(session) };
}

export async function beginSetup(input: {
  setupToken: string;
  email: string;
  password: string;
  displayName?: string;
}): Promise<
  | {
      ok: true;
      otpauthUri: string;
      enrollmentId?: string;
      user?: SessionUser;
      /** In-memory only — complements HttpOnly setup cookie for cross-origin. */
      setupChallengeToken?: string;
    }
  | { ok: false; message: string; status: number; code?: string }
> {
  const res = await authFetch("/v1/auth/setup/begin", {
    method: "POST",
    body: JSON.stringify({
      setupToken: input.setupToken,
      email: input.email,
      password: input.password,
      ...(input.displayName !== undefined && input.displayName !== ""
        ? { displayName: input.displayName }
        : {}),
    }),
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody & {
    otpauthUri?: string;
    enrollmentId?: string;
    user?: SessionUser;
    setupChallengeToken?: string;
  };
  if (!res.ok || !body.otpauthUri) {
    return {
      ok: false,
      status: res.status,
      code: body.error?.code,
      message: readErrorMessage(body, "Setup could not be started"),
    };
  }
  return {
    ok: true,
    otpauthUri: body.otpauthUri,
    enrollmentId: body.enrollmentId,
    user: body.user,
    setupChallengeToken:
      typeof body.setupChallengeToken === "string" && body.setupChallengeToken.trim()
        ? body.setupChallengeToken
        : undefined,
  };
}

export async function confirmSetupTotp(
  code: string,
  setupChallengeToken?: string
): Promise<
  | ({ ok: true; recoveryCodes: string[] } & AuthSessionResult)
  | { ok: false; message: string; status: number; code?: string }
> {
  const res = await authFetch("/v1/auth/setup/totp/confirm", {
    method: "POST",
    body: JSON.stringify({
      totpCode: code,
      ...(setupChallengeToken?.trim()
        ? { setupChallengeToken: setupChallengeToken.trim() }
        : {}),
    }),
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody &
    Record<string, unknown> & { recoveryCodes?: string[] };
  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      code: body.error?.code,
      message: readErrorMessage(body, "Setup confirmation failed"),
    };
  }
  const session = parseSessionEnvelope(body);
  const recoveryCodes = Array.isArray(body.recoveryCodes)
    ? body.recoveryCodes.filter((c): c is string => typeof c === "string")
    : [];
  if (!session || recoveryCodes.length === 0) {
    return { ok: false, status: res.status, message: "Setup confirmation failed" };
  }
  return { ok: true, recoveryCodes, ...applySession(session) };
}

export async function beginRecovery(): Promise<
  | { ok: true; recoveryRequired: true }
  | { ok: false; message: string; status: number; code?: string }
> {
  const res = await authFetch("/v1/auth/mfa/recovery/begin", {
    method: "POST",
    body: "{}",
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody & {
    recoveryRequired?: boolean;
  };
  if (!res.ok || body.recoveryRequired !== true) {
    return {
      ok: false,
      status: res.status,
      code: body.error?.code,
      message: readErrorMessage(body, "Recovery could not be started"),
    };
  }
  return { ok: true, recoveryRequired: true };
}

export async function recoveryVerify(
  code: string
): Promise<
  | { ok: true; reenrollRequired: true; user: SessionUser }
  | { ok: false; message: string; status: number; code?: string }
> {
  const res = await authFetch("/v1/auth/mfa/recovery/verify", {
    method: "POST",
    body: JSON.stringify({ recoveryCode: code }),
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody & {
    reenrollRequired?: boolean;
    user?: SessionUser;
  };
  if (!res.ok || body.reenrollRequired !== true || !body.user) {
    return {
      ok: false,
      status: res.status,
      code: body.error?.code,
      message: readErrorMessage(body, "Recovery verification failed"),
    };
  }
  return { ok: true, reenrollRequired: true, user: body.user };
}

export async function beginReenroll(): Promise<
  | { ok: true; otpauthUri: string }
  | { ok: false; message: string; status: number; code?: string }
> {
  const res = await authFetch("/v1/auth/setup/totp/reenroll/begin", {
    method: "POST",
    body: "{}",
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody & {
    otpauthUri?: string;
  };
  if (!res.ok || !body.otpauthUri) {
    return {
      ok: false,
      status: res.status,
      code: body.error?.code,
      message: readErrorMessage(body, "Re-enrollment could not be started"),
    };
  }
  return { ok: true, otpauthUri: body.otpauthUri };
}

export async function confirmReenroll(
  code: string
): Promise<
  | ({ ok: true; recoveryCodes: string[] } & AuthSessionResult)
  | { ok: false; message: string; status: number; code?: string }
> {
  const res = await authFetch("/v1/auth/setup/totp/reenroll/confirm", {
    method: "POST",
    body: JSON.stringify({ totpCode: code }),
  });
  const body = (await res.json().catch(() => ({}))) as ApiErrorBody &
    Record<string, unknown> & { recoveryCodes?: string[] };
  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      code: body.error?.code,
      message: readErrorMessage(body, "Re-enrollment confirmation failed"),
    };
  }
  const session = parseSessionEnvelope(body);
  const recoveryCodes = Array.isArray(body.recoveryCodes)
    ? body.recoveryCodes.filter((c): c is string => typeof c === "string")
    : [];
  if (!session || recoveryCodes.length === 0) {
    return {
      ok: false,
      status: res.status,
      message: "Re-enrollment confirmation failed",
    };
  }
  return { ok: true, recoveryCodes, ...applySession(session) };
}

export async function logoutSession(): Promise<void> {
  try {
    await authFetch("/v1/auth/logout", { method: "POST", body: "{}" });
  } finally {
    setMemoryCsrfToken(null);
  }
}
